import React, { useEffect, useRef, useState, useMemo } from "react";
import "./css/main.css";
import "./css/issuance.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faUser, faPlus } from "@fortawesome/free-solid-svg-icons";
import { QRCodeSVG } from "qrcode.react";

import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { useNavigate, useLocation } from "react-router-dom";

// 등급별 배경색 (목업 데이터의 grade 값에 따라 사용)
// 나중에 색상만 바꾸고 싶으면 여기만 수정하면 됨
export const GRADE_COLORS = {
    1: "linear-gradient(160deg,#f26a5a 0%,#e74f6a 55%,#d2457a 100%)", // 1등급: 자홍색
    2: "linear-gradient(160deg,#4dd0e1 0%,#00bcd4 55%,#0097a7 100%)", // 2등급
    3: "linear-gradient(160deg,#5567f4 0%,#4850d9 55%,#3d3abf 100%)", // 3등급(기본)
};

// 휴대폰/유선 전화번호 자동 하이픈 포맷 (login.js/setting.js와 동일)
function formatPhoneKOR(input = "") {
    const v = String(input).replace(/\D/g, "");
    if (!v) return "";
    if (v.startsWith("02")) {
        if (v.length <= 2) return v;
        if (v.length <= 5) return `02-${v.slice(2)}`;
        if (v.length <= 9) return `02-${v.slice(2, 5)}-${v.slice(5)}`;
        return `02-${v.slice(2, 6)}-${v.slice(6, 10)}`;
    }
    const head3 = v.slice(0, 3);
    if (v.length <= 3) return head3;
    if (v.length <= 7) return `${head3}-${v.slice(3)}`;
    if (v.length <= 10) return `${head3}-${v.slice(3, 6)}-${v.slice(6, 10)}`;
    return `${head3}-${v.slice(3, 7)}-${v.slice(7, 11)}`;
}

export default function Main() {
    const navigate = useNavigate();
    const location = useLocation();
    const swiperRef = useRef(null);
    const editImageInputRef = useRef(null);

    const [shareOpen, setShareOpen] = useState(false);
    const closeShare = () => setShareOpen(false);

    // flippedIdx: 자격증 카드 인덱스, -1 은 모바일 신분증 카드
    const [flippedIdx, setFlippedIdx] = useState(null);
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") {
                setShareOpen(false);
                setFlippedIdx(null);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    // issuance에서 돌아올 때 특정 슬라이드로 이동, 또는 기본적으로 인덱스 1로 이동
    useEffect(() => {
        if (swiperRef.current) {
            setTimeout(() => {
                if (swiperRef.current) {
                    if (location.state?.slideIndex !== undefined) {
                        swiperRef.current.swiper.slideTo(location.state.slideIndex, 300);
                    } else {
                        // location.state가 없으면 항상 인덱스 1로 이동 (+ 카드 오른쪽)
                        swiperRef.current.swiper.slideTo(1, 300);
                    }
                }
            }, 100);
        }
    }, [location.state, location.pathname]);

    // 모바일 신분증용 사용자 정보 상태
    const [userInfo, setUserInfo] = useState({
        name: "",
        picture: null,
        profileImg: null,
        birthday: null,
        position: "",
        church: "",
        yearAssociation: "",
        localAssociation: "",
        email: "",
        phone: "",
        level: null,
    });

    // 로딩 상태 추적
    const [isLoading, setIsLoading] = useState(true);

    // 등급과 관계없이 고정 배경 이미지 사용
    const getBackgroundImage = () => "/img/bg_img_blue.png";

    // 사용자 정보 로드 (이메일로 무조건 호출)
    // location이 변경될 때마다 (활성화 후 /main으로 이동할 때 포함) 데이터를 다시 불러옴
    useEffect(() => {
        const loadUserInfo = async () => {
            setIsLoading(true); // 로딩 시작
            const API_BASE_URL =
                process.env.REACT_APP_API_BASE_URL || "http://localhost:4000/api";

            // localStorage에서 이메일 가져오기
            const email = localStorage.getItem("hb_serverEmail") ||
                localStorage.getItem("hb_googleEmail") ||
                "";

            if (!email) {
                console.log("Main 페이지: 이메일이 없어서 사용자 정보를 가져올 수 없습니다.");
                // Fallback to localStorage
                const savedName =
                    localStorage.getItem("hb_serverName") ||
                    localStorage.getItem("hb_googleName") ||
                    "";
                const savedPicture =
                    localStorage.getItem("hb_googlePicture") || null;
                const savedBirthday = localStorage.getItem("hb_birthday") || null;
                setUserInfo((prev) => ({
                    ...prev,
                    name: savedName,
                    picture: savedPicture,
                    birthday: savedBirthday,
                }));
                setIsLoading(false); // 로딩 완료
                return;
            }

            // 이메일로 사용자 정보 조회 (토큰 검증 없음)
            try {
                // console.log("Main 페이지: 이메일로 사용자 정보 조회 시작:", email);
                const res = await fetch(`${API_BASE_URL}/auth/user-by-email?email=${encodeURIComponent(email)}`, {
                    method: "GET",
                });

                if (res.ok) {
                    const data = await res.json();
                    const user = data.user || {};

                    // console.log("Main 페이지: 서버 응답 데이터:", data);
                    // console.log("Main 페이지: user 객체:", user);
                    // console.log("Main 페이지: user.birthday:", user.birthday);

                    // 서버에서 받은 최신 정보를 state에 설정
                    // 이미지 우선순위: profileImg > picture > null
                    setUserInfo((prev) => ({
                        ...prev,
                        name: user.name || "",
                        picture: user.picture || null,
                        profileImg: user.profileImg || null,
                        birthday: user.birthday || null,
                        position: user.position || "",
                        church: user.church || "",
                        yearAssociation: user.yearAssociation || user.year_association || "",
                        localAssociation: user.localAssociation || user.local_association || "",
                        email: user.email || "",
                        phone: user.phone || "",
                        level: user.level || null,
                    }));

                    // 등급 정보를 localStorage에 저장
                    if (user.level != null) {
                        localStorage.setItem("hb_userLevel", String(user.level));
                    }

                    // 서버에서 받은 정보를 localStorage에도 업데이트
                    if (user.name) {
                        localStorage.setItem("hb_serverName", user.name);
                        localStorage.setItem("hb_googleName", user.name);
                    }
                    if (user.email) {
                        localStorage.setItem("hb_serverEmail", user.email);
                        localStorage.setItem("hb_googleEmail", user.email);
                    }
                    if (user.picture) {
                        localStorage.setItem("hb_googlePicture", user.picture);
                    }
                    // birthday는 null이어도 저장 (명시적으로 처리)
                    if (user.birthday !== undefined) {
                        localStorage.setItem("hb_birthday", user.birthday || "");
                    }
                    if (user.id) {
                        localStorage.setItem("hb_backendUserId", String(user.id));
                    }

                    // console.log("Main 페이지: 서버에서 최신 사용자 정보를 가져왔습니다.", user);
                    // console.log("Main 페이지: 저장된 birthday:", user.birthday);
                    setIsLoading(false); // 로딩 완료
                    return;
                } else {
                    console.error("사용자 정보 로드 실패:", res.status);
                }
            } catch (e) {
                console.error("Failed to load user info from server", e);
            }

            // Fallback to localStorage (서버 호출 실패 시)
            const savedName =
                localStorage.getItem("hb_serverName") ||
                localStorage.getItem("hb_googleName") ||
                "";
            const savedPicture =
                localStorage.getItem("hb_googlePicture") || null;
            const savedBirthday = localStorage.getItem("hb_birthday") || null;
            setUserInfo((prev) => ({
                ...prev,
                name: savedName,
                picture: savedPicture,
                profileImg: null,
                birthday: savedBirthday,
            }));
            setIsLoading(false); // 로딩 완료
        };

        loadUserInfo();
    }, [location.pathname]); // location.pathname이 변경될 때마다 데이터를 다시 불러옴

    // 대기중인 자격증 상태
    const [pendingCert, setPendingCert] = useState(null);
    const [pendingCerts, setPendingCerts] = useState([]);
    
    // 승인/거절 모달 상태
    const [approvalModal, setApprovalModal] = useState({ open: false, cert: null, type: null }); // type: 'mobile' or 'other'
    
    // 수정 모달 상태
    const [editModal, setEditModal] = useState({ open: false, cert: null, type: null, draft: null });
    
    // 삭제 확인 모달 상태
    const [deleteModal, setDeleteModal] = useState({ open: false, cert: null, type: null });
    
    // 승인된 자격증이 있는지 확인 (visible이 false가 아닌 것만)
    const hasApprovedCert = useMemo(() => {
        const pendingCertData = localStorage.getItem("hb_pending_cert");
        if (pendingCertData) {
            try {
                const data = JSON.parse(pendingCertData);
                if (data.status === "approved" && data.visible !== false) {
                    return true;
                }
            } catch (e) {
                console.error("Failed to parse pending cert:", e);
            }
        }
        
        const pendingCertsData = localStorage.getItem("hb_pending_certs");
        if (pendingCertsData) {
            try {
                const data = JSON.parse(pendingCertsData);
                if (Array.isArray(data)) {
                    return data.some(cert => cert.status === "approved" && cert.visible !== false);
                }
            } catch (e) {
                console.error("Failed to parse pending certs:", e);
            }
        }
        
        return false;
    }, [pendingCert, pendingCerts]);
    
    // 기본 신분증 표시 여부 확인 (location.pathname 변경 시 재계산)
    const shouldShowDefaultCert = useMemo(() => {
        const defaultCertVisible = localStorage.getItem("hb_default_cert_visible");
        // 설정에서 명시적으로 "true"로 설정하면 항상 보임
        if (defaultCertVisible === "true") {
            return true;
        }
        // 설정에서 명시적으로 "false"로 설정하면 안 보임
        if (defaultCertVisible === "false") {
            return false;
        }
        // 기본값: 승인된 자격증이 없으면 보임
        return !hasApprovedCert;
    }, [hasApprovedCert, location.pathname]);

    useEffect(() => {
        // 1) 탐정법인홍익 모바일 신분증: 신분증 신청(hb_pending_cert)이 있을 때만 메인에 카드 표시.
        //    가입신청만(hb_mobile_join_info) 한 경우에는 카드를 표시하지 않음.
        const pendingCertData = localStorage.getItem("hb_pending_cert");
        if (pendingCertData) {
            try {
                const data = JSON.parse(pendingCertData);
                if (data.name === "탐정법인홍익 모바일 신분증" && (data.status === "pending" || data.status === "approved") && data.visible !== false) {
                    setPendingCert(data);
                } else {
                    setPendingCert(null);
                }
            } catch (e) {
                console.error("Failed to parse pending cert:", e);
                setPendingCert(null);
            }
        } else {
            setPendingCert(null);
        }

        // 2) 기타 자격증: pending 데이터 우선, 없으면 가입 정보로 대체
        const pendingCertsData = localStorage.getItem("hb_pending_certs");
        let appliedCerts = [];
        if (pendingCertsData) {
            try {
                const data = JSON.parse(pendingCertsData);
                if (Array.isArray(data)) {
                    appliedCerts = data.filter(cert =>
                        (cert.status === "pending" || cert.status === "approved") &&
                        cert.visible !== false
                    );
                }
            } catch (e) {
                console.error("Failed to parse pending certs:", e);
            }
        }

        // 가입 정보만 있고 자격증 신청이 없으면 자격증을 표시하지 않음
        setPendingCerts(appliedCerts);
    }, [location.pathname]);

    // 승인 처리
    const handleApprove = () => {
        if (approvalModal.type === 'mobile') {
            const updatedCert = { ...approvalModal.cert, status: "approved" };
            localStorage.setItem("hb_pending_cert", JSON.stringify(updatedCert));
            setPendingCert(updatedCert);
        } else {
            const pendingCertsData = localStorage.getItem("hb_pending_certs");
            if (pendingCertsData) {
                try {
                    const data = JSON.parse(pendingCertsData);
                    const updatedCerts = data.map(cert => 
                        cert.name === approvalModal.cert.name && cert.status === "pending"
                            ? { ...cert, status: "approved" }
                            : cert
                    );
                    localStorage.setItem("hb_pending_certs", JSON.stringify(updatedCerts));
                    setPendingCerts(updatedCerts.filter(cert => cert.status === "pending" || cert.status === "approved"));
                } catch (e) {
                    console.error("Failed to update pending certs:", e);
                }
            }
        }
        setApprovalModal({ open: false, cert: null, type: null });
    };

    // 거절 처리
    const handleReject = () => {
        if (approvalModal.type === 'mobile') {
            localStorage.removeItem("hb_pending_cert");
            setPendingCert(null);
        } else {
            const pendingCertsData = localStorage.getItem("hb_pending_certs");
            if (pendingCertsData) {
                try {
                    const data = JSON.parse(pendingCertsData);
                    const updatedCerts = data.filter(cert => 
                        !(cert.name === approvalModal.cert.name && cert.status === "pending")
                    );
                    localStorage.setItem("hb_pending_certs", JSON.stringify(updatedCerts));
                    setPendingCerts(updatedCerts.filter(cert => cert.status === "pending" || cert.status === "approved"));
                } catch (e) {
                    console.error("Failed to remove pending cert:", e);
                }
            }
        }
        setApprovalModal({ open: false, cert: null, type: null });
    };

    // 수정 모달 열기
    const handleEditClick = (cert, type) => {
        setEditModal({
            open: true,
            cert: cert,
            type: type,
            draft: { ...cert }
        });
    };

    // 이미지 변경 핸들러 (수정 모달용)
    const handleEditImageChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result;
                setEditModal(prev => ({
                    ...prev,
                    draft: { ...prev.draft, image: base64String }
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    // 이미지 제거 핸들러 (수정 모달용)
    const handleEditImageRemove = () => {
        setEditModal(prev => ({
            ...prev,
            draft: { ...prev.draft, image: null }
        }));
        if (editImageInputRef.current) {
            editImageInputRef.current.value = '';
        }
    };

    // 수정 저장
    const handleEditSave = () => {
        if (editModal.type === 'mobile') {
            const updatedCert = { ...editModal.draft, status: "approved" };
            localStorage.setItem("hb_pending_cert", JSON.stringify(updatedCert));
            setPendingCert(updatedCert);
        } else {
            const pendingCertsData = localStorage.getItem("hb_pending_certs");
            if (pendingCertsData) {
                try {
                    const data = JSON.parse(pendingCertsData);
                    const updatedCerts = data.map(cert => 
                        cert.name === editModal.cert.name && cert.status === "approved"
                            ? { ...editModal.draft, status: "approved" }
                            : cert
                    );
                    localStorage.setItem("hb_pending_certs", JSON.stringify(updatedCerts));
                    setPendingCerts(updatedCerts.filter(cert => cert.status === "pending" || cert.status === "approved"));
                } catch (e) {
                    console.error("Failed to update pending certs:", e);
                }
            }
        }
        setEditModal({ open: false, cert: null, type: null, draft: null });
    };

    // 삭제 확인 모달 열기
    const handleDeleteClick = (cert, type) => {
        setDeleteModal({ open: true, cert: cert, type: type });
    };

    // 삭제 확인 후 삭제
    const handleDeleteConfirm = () => {
        if (deleteModal.type === 'mobile') {
            // 탐정법인홍익 모바일 신분증 삭제 시 모든 관련 정보 삭제
            localStorage.removeItem("hb_pending_cert");
            localStorage.removeItem("hb_mobile_join_info");
            localStorage.removeItem("hb_mobile_id_joined");
            setPendingCert(null);
        } else {
            // 다른 자격증 삭제 시 모든 관련 정보 삭제
            const certName = deleteModal.cert.name;
            const pendingCertsData = localStorage.getItem("hb_pending_certs");
            if (pendingCertsData) {
                try {
                    const data = JSON.parse(pendingCertsData);
                    const updatedCerts = data.filter(cert => 
                        !(cert.name === certName && cert.status === "approved")
                    );
                    localStorage.setItem("hb_pending_certs", JSON.stringify(updatedCerts));
                    setPendingCerts(updatedCerts.filter(cert => cert.status === "pending" || cert.status === "approved"));
                } catch (e) {
                    console.error("Failed to remove pending cert:", e);
                }
            }
            
            // 일반 자격증 가입 정보 확인 및 삭제
            const generalJoinInfo = localStorage.getItem("hb_general_join_info");
            if (generalJoinInfo) {
                try {
                    const info = JSON.parse(generalJoinInfo);
                    if (info.name === certName) {
                        localStorage.removeItem("hb_general_join_info");
                        localStorage.removeItem("hb_general_joined");
                    }
                } catch (e) {
                    console.error("Failed to parse hb_general_join_info", e);
                }
            }
        }
        setDeleteModal({ open: false, cert: null, type: null });
    };

    // ✅ 목업 데이터 (접두어 없이 값만)
    const banners = [
        // {
        //     type: "검정 중",
        //     number: "2020-005215",
        //     name: "클라이밍지도사",
        //     org: "대한스포츠클라이밍협회",
        //     status: "시험대기중",
        //     detail: "2025-11-16 오전 10시\n양재고등학교",
        //     acquired: "",
        //     renew: "",
        //     cta: "",
        //     grade: 1,
        // },
        // {
        //     type: "보유자격증",
        //     number: "2021-001122",
        //     name: "아로마컨설턴트 2급",
        //     org: "해피브릿지",
        //     status: "",
        //     detail: "",
        //     acquired: "2024-03-02",
        //     renew: "2026-01-01\n~ 2026-12-31",
        //     cta: "공유하기",
        //     grade: 2,
        // },
        // {
        //     type: "보유자격증",
        //     number: "2020-005262",
        //     name: "클라이밍루트세팅전문가",
        //     org: "대한스포츠클라이밍협회",
        //     status: "",
        //     detail: "",
        //     acquired: "2024-10-13",
        //     renew: "2027-01-01\n~ 2027-12-31",
        //     cta: "공유하기",
        //     grade: 3,
        // },
        // {
        //     type: "보유자격증",
        //     number: "2020-003260",
        //     name: "스포츠클라이밍안전관리사",
        //     org: "대한스포츠클라이밍협회",
        //     status: "",
        //     detail: "",
        //     acquired: "2024-10-13",
        //     renew: "2027-01-01\n~ 2027-12-31",
        //     cta: "공유하기",
        //     grade: 3,
        // },
    ];

    const toggleFlip = (i) => setFlippedIdx((p) => (p === i ? null : i));
    const onShareClick = (e) => {
        e.stopPropagation();
        setShareOpen(true);
    };

    return (
        <div className="container-wrap main-wrap">
            <img src={getBackgroundImage()} className="main-background-image" />
            <div className="main-viewport">
                <header className="setting-header">
                    <img
                        src="/img/hongik_logo.png"
                        alt="탐정법인홍익"
                        className="setting-logo"
                    // onClick={() => navigate("/")}
                    />
                </header>
                <main className="main-content">
                    <div className="main-swiper-area">
                        <Swiper
                            ref={swiperRef}
                            slidesPerView={1.2}
                            centeredSlides
                            spaceBetween={16}
                            pagination={{ clickable: true, dynamicBullets: true }}
                            modules={[Pagination]}
                            loop={false}
                            slidesPerGroup={1}
                            freeMode={false}
                            longSwipes
                            longSwipesRatio={0.2}
                            longSwipesMs={180}
                            threshold={8}
                            speed={350}
                            initialSlide={location.state?.slideIndex !== undefined ? location.state.slideIndex : 1}
                            allowSlidePrev={true}
                            allowSlideNext={true}
                            onSlideChange={(swiper) => {
                                setFlippedIdx(null);
                            }}
                            className="main-swiper"
                        >
                            {/* + 카드 (첫 번째 슬라이드) */}
                            <SwiperSlide className="main-slide">
                                <article
                                    className="main-card main-add-card"
                                    onClick={() => {
                                        navigate("/search");
                                    }}
                                    style={{
                                        cursor: "pointer",
                                        background: "rgba(255, 255, 255, 0.4)",
                                        backdropFilter: "blur(5px) saturate(1.4)",
                                        WebkitBackdropFilter: "blur(5px) saturate(1.4)",
                                        border: "1px solid rgba(255, 255, 255, 0.7)",
                                        boxShadow: "0 0px 10px rgba(15, 23, 42, 0.3)",
                                    }}
                                >
                                    <div className="main-card-inner">
                                        <div
                                            className="main-card-face main-card-front"
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                background: "transparent",
                                                border: "none",
                                                boxShadow: "none",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: "80px",
                                                    height: "80px",
                                                    borderRadius: "50%",
                                                    backgroundColor: "rgba(255, 255, 255, 0.6)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    marginBottom: "20px",
                                                    border: "2px solid rgba(255, 255, 255, 0.8)",
                                                }}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faPlus}
                                                    style={{
                                                        fontSize: "40px",
                                                        color: "#4285f4",
                                                    }}
                                                />
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: "1.125em",
                                                    fontWeight: "600",
                                                    color: "#333",
                                                    textAlign: "center",
                                                }}
                                            >
                                                새 카드 추가
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            </SwiperSlide>

                            {/* 메인 신분증 카드 (두 번째) - 기본 신분증 UI 사용 - 기본 자격증이 표시되어야 할 때만 표시 (설정에서 노출로 변경하면 보임) */}
                            {shouldShowDefaultCert && (
                            <SwiperSlide className="main-slide">
                                <article
                                    className={`main-card main-id-card ${flippedIdx === "basic" ? "is-flipped" : ""}`}
                                    onClick={() => toggleFlip("basic")}
                                >
                                    <div className="main-card-inner shake-on-open">
                                        {/* ===== 앞면: 기본 신분증 ===== */}
                                        <div
                                            className="main-card-face main-card-front"
                                            style={{
                                                background: "rgba(255, 255, 255, 0.4)",
                                                backdropFilter: "blur(5px) saturate(1.4)",
                                                WebkitBackdropFilter: "blur(5px) saturate(1.4)",
                                                border: "1px solid rgba(255, 255, 255, 0.7)",
                                                boxShadow: "0 0px 10px rgba(15, 23, 42, 0.3)",
                                            }}
                                        >
                                            <div className="main-id-card-content">
                                                <div className="main-id-card-body" style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
                                                    <div className="main-id-title">
                                                        모바일 신분증
                                                    </div>
                                                    <div className="main-id-sbtitle">
                                                        Mobile Identification
                                                    </div>

                                                    <div className="main-id-photo-container" style={{ padding: "0" }}>
                                                        <div className="main-id-photo-placeholder">
                                                            <FontAwesomeIcon icon={faUser} className="main-id-photo-icon" />
                                                        </div>
                                                    </div>

                                                    {/* 이름 */}
                                                    {isLoading ? (
                                                        <div className="main-id-name-skeleton"></div>
                                                    ) : (
                                                        <div className="main-id-name">
                                                            {(userInfo.name || "이름").split('').map((char, index, array) => (
                                                                <span key={index} className="main-id-name-char" style={{ marginRight: index < array.length - 1 ? '0.4em' : '0' }}>
                                                                    {char}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* 이메일 */}
                                                    {isLoading ? (
                                                        <div className="main-id-info-skeleton"></div>
                                                    ) : (
                                                        <div className="main-id-birthday">
                                                            <span className="value">{userInfo.email || "-"}</span>
                                                        </div>
                                                    )}

                                                    {/* 전화번호 */}
                                                    {isLoading ? (
                                                        <div className="main-id-info-skeleton"></div>
                                                    ) : (
                                                        <div className="main-id-birthday">
                                                            <span className="value">
                                                                {userInfo.phone
                                                                    ? formatPhoneKOR(userInfo.phone)
                                                                    : "-"}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* ===== 뒷면: 기본 신분증 상세 ===== */}
                                        <div className="main-card-face main-card-back"
                                            style={{
                                                background: "linear-gradient(160deg, rgb(11, 58, 111) 0%, rgb(13, 43, 83) 60%, rgb(8, 34, 63) 100%)",
                                                color: "#ffffff",
                                                border: "1px solid rgba(255, 255, 255, 0.7)",
                                                boxShadow: "0 0px 10px rgba(15, 23, 42, 0.6)",
                                            }}>
                                            <div className="main-back-content"
                                                style={{
                                                    gap: "0",
                                                    color: "#ffffff"
                                                }}>
                                                <h3 className="main-back-title cert"
                                                    style={{
                                                        textAlign: "center",
                                                        background: "transparent",
                                                        color: "#ffffff"
                                                    }}>상세정보</h3>
                                                <ul className="main-back-list"
                                                    style={{
                                                        padding: "0 18px",
                                                        color: "#ffffff"
                                                    }}>
                                                    <li>
                                                        <span className="k" style={{ color: "#ffffff" }}>이름</span>
                                                        <span className="v" style={{ color: "#ffffff" }}>{userInfo.name || "-"}</span>
                                                    </li>
                                                    <li>
                                                        <span className="k" style={{ color: "#ffffff" }}>이메일</span>
                                                        <span className="v" style={{ color: "#ffffff" }}>{userInfo.email || "-"}</span>
                                                    </li>
                                                    <li>
                                                        <span className="k" style={{ color: "#ffffff" }}>전화번호</span>
                                                        <span className="v" style={{ color: "#ffffff" }}>
                                                            {userInfo.phone
                                                                ? formatPhoneKOR(userInfo.phone)
                                                                : "-"}
                                                        </span>
                                                    </li>
                                                </ul>

                                                <div className="main-back-actions">
                                                    <>
                                                        <div></div>
                                                        <button
                                                            type="button"
                                                            className="main-back-btn outline"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleFlip("basic");
                                                            }}
                                                        >
                                                            닫기
                                                        </button>
                                                    </>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            </SwiperSlide>
                            )}

                            {/* 기존 모바일 신분증 카드 (주석처리) */}
                            {/* <SwiperSlide className="main-slide">
                                <article
                                    className={`main-card main-id-card ${flippedIdx === -1 ? "is-flipped" : ""}`}
                                    onClick={() => toggleFlip(-1)}
                                >
                                    <div className="main-card-inner">
                                        <div
                                            className="main-card-face main-card-front"
                                            style={{
                                                background: "rgba(255, 255, 255, 0.4)",
                                                backdropFilter: "blur(5px) saturate(1.4)",
                                                WebkitBackdropFilter: "blur(5px) saturate(1.4)",
                                                border: "1px solid rgba(255, 255, 255, 0.7)",
                                                boxShadow: "0 0px 10px rgba(15, 23, 42, 0.3)",
                                            }}
                                        >
                                            <div className="main-id-card-content">
                                                <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
                                                    <div className="main-id-title" style={{}}>
                                                        모바일 신분증
                                                    </div>
                                                    <div className="main-id-sbtitle" style={{}}>
                                                        Mobile Identification
                                                    </div>

                                                    <div className="main-id-photo-container" style={{ padding: "0" }}>
                                                        {isLoading ? (
                                                            <div className="main-id-photo-skeleton"></div>
                                                        ) : userInfo.profileImg ? (
                                                            <img
                                                                src={userInfo.profileImg.startsWith('http') 
                                                                    ? userInfo.profileImg 
                                                                    : `https://sv.hongik.mycert.or.kr${userInfo.profileImg.startsWith('/') ? userInfo.profileImg : '/' + userInfo.profileImg}`}
                                                                alt="프로필"
                                                                className="main-id-photo"
                                                            />
                                                        ) : userInfo.picture ? (
                                                            <img
                                                                src={userInfo.picture}
                                                                alt="프로필"
                                                                className="main-id-photo"
                                                            />
                                                        ) : (
                                                            <div className="main-id-photo-placeholder">
                                                                <FontAwesomeIcon icon={faUser} className="main-id-photo-icon" />
                                                            </div>
                                                        )}
                                                    </div>

                                                    {isLoading ? (
                                                        <div className="main-id-name-skeleton"></div>
                                                    ) : (
                                                        <div className="main-id-name">
                                                            {(userInfo.name || "이름").split('').map((char, index, array) => (
                                                                <span key={index} className="main-id-name-char" style={{ marginRight: index < array.length - 1 ? '0.4em' : '0' }}>
                                                                    {char}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {isLoading ? (
                                                        <div className="main-id-info-skeleton"></div>
                                                    ) : (
                                                        <div className="main-id-birthday">
                                                            {userInfo.church && userInfo.position ? (
                                                                <>
                                                                    <span className="value">{userInfo.church}</span>
                                                                    <span className="separator">|</span>
                                                                    <span className="value">{userInfo.position}</span>
                                                                </>
                                                            ) : "-"}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="main-card-face main-card-back"
                                            style={{
                                                background: "linear-gradient(160deg, rgb(11, 58, 111) 0%, rgb(13, 43, 83) 60%, rgb(8, 34, 63) 100%)",
                                                color: "#ffffff",
                                                border: "1px solid rgba(255, 255, 255, 0.7)",
                                                boxShadow: "0 0px 10px rgba(15, 23, 42, 0.6)",
                                            }}>
                                            <div className="main-back-content"
                                                style={{
                                                    gap: "0",
                                                    color: "#ffffff"
                                                }}>
                                                <h3 className="main-back-title cert"
                                                    style={{
                                                        textAlign: "center",
                                                        background: "transparent",
                                                        color: "#ffffff"
                                                    }}>상세정보</h3>
                                                <ul className="main-back-list"
                                                    style={{
                                                        padding: "0 18px",
                                                        color: "#ffffff"
                                                    }}>
                                                    <li>
                                                        <span className="k" style={{ color: "#ffffff" }}>소속 연회</span>
                                                        <span className="v" style={{ color: "#ffffff" }}>{userInfo.yearAssociation || "-"}</span>
                                                    </li>
                                                    <li>
                                                        <span className="k" style={{ color: "#ffffff" }}>소속 지방회</span>
                                                        <span className="v" style={{ color: "#ffffff" }}>{userInfo.localAssociation || "-"}</span>
                                                    </li>
                                                    <li>
                                                        <span className="k" style={{ color: "#ffffff" }}>소속 교회</span>
                                                        <span className="v" style={{ color: "#ffffff" }}>{userInfo.church || "-"}</span>
                                                    </li>
                                                    <li>
                                                        <span className="k" style={{ color: "#ffffff" }}>이름</span>
                                                        <span className="v" style={{ color: "#ffffff" }}>{userInfo.name || "-"}</span>
                                                    </li>
                                                    <li>
                                                        <span className="k" style={{ color: "#ffffff" }}>이메일</span>
                                                        <span className="v" style={{ color: "#ffffff" }}>{userInfo.email || "-"}</span>
                                                    </li>
                                                    <li>
                                                        <span className="k" style={{ color: "#ffffff" }}>전화번호</span>
                                                        <span className="v" style={{ color: "#ffffff" }}>
                                                            {userInfo.phone
                                                                ? formatPhoneKOR(userInfo.phone)
                                                                : "-"}
                                                        </span>
                                                    </li>
                                                    <li>
                                                        <span className="k" style={{ color: "#ffffff" }}>호칭</span>
                                                        <span className="v" style={{ color: "#ffffff" }}>{userInfo.position || "-"}</span>
                                                    </li>
                                                </ul>

                                                <div className="main-back-actions">
                                                    <>
                                                        <div></div>
                                                        <button
                                                            type="button"
                                                            className="main-back-btn outline"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleFlip(-1);
                                                            }}
                                                        >
                                                            닫기
                                                        </button>
                                                    </>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            </SwiperSlide> */}

                            {/* 발급 신청한 모바일 신분증 카드 (모바일 신분증과 동일한 형태) */}
                            {pendingCert && pendingCert.name === "탐정법인홍익 모바일 신분증" && (
                                <SwiperSlide className="main-slide">
                                    <article
                                        className={`main-card main-id-card ${pendingCert.status === "pending" ? "main-card-pending" : ""} ${flippedIdx === "pending" ? "is-flipped" : ""}`}
                                        style={{ cursor: pendingCert.status === "pending" ? "default" : "pointer" }}
                                        onClick={() => {
                                            if (pendingCert.status === "approved") {
                                                toggleFlip("pending");
                                            }
                                        }}
                                    >
                                        {/* 관리자 승인 대기 오버레이 (pending일 때만 표시) */}
                                        {pendingCert.status === "pending" && (
                                            <div 
                                                className="main-card-pending-overlay"
                                                onClick={() => setApprovalModal({ open: true, cert: pendingCert, type: 'mobile' })}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <div className="main-card-pending-message">
                                                    관리자 승인 대기중입니다.
                                                </div>
                                            </div>
                                        )}
                                        <div className="main-card-inner">
                                            {/* ===== 앞면: 모바일 신분증 ===== */}
                                            <div
                                                className="main-card-face main-card-front"
                                                style={{
                                                    background: "rgba(255, 255, 255, 0.4)",
                                                    backdropFilter: "blur(5px) saturate(1.4)",
                                                    WebkitBackdropFilter: "blur(5px) saturate(1.4)",
                                                    border: "1px solid rgba(255, 255, 255, 0.7)",
                                                    boxShadow: "0 0px 10px rgba(15, 23, 42, 0.3)",
                                                }}
                                            >
                                                <div className="main-id-card-content">
                                                    <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
                                                        <div className="main-id-title">
                                                            모바일 신분증
                                                        </div>
                                                        <div className="main-id-sbtitle">
                                                            Mobile Identification
                                                        </div>

                                                        <div className="main-id-photo-container" style={{ padding: "0" }}>
                                                            {pendingCert.image ? (
                                                                <img
                                                                    src={pendingCert.image}
                                                                    alt="프로필"
                                                                    className="main-id-photo"
                                                                />
                                                            ) : (
                                                                <div className="main-id-photo-placeholder">
                                                                    <FontAwesomeIcon icon={faUser} className="main-id-photo-icon" />
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* 이름 */}
                                                        <div className="main-id-name">
                                                            {(userInfo.name || "이름").split('').map((char, index, array) => (
                                                                <span key={index} className="main-id-name-char" style={{ marginRight: index < array.length - 1 ? '0.4em' : '0' }}>
                                                                    {char}
                                                                </span>
                                                            ))}
                                                        </div>

                                                        {/* 교회/직책 */}
                                                        <div className="main-id-birthday">
                                                            {pendingCert.church && pendingCert.position ? (
                                                                <>
                                                                    <span className="value">{pendingCert.church}</span>
                                                                    <span className="separator">|</span>
                                                                    <span className="value">{pendingCert.position}</span>
                                                                </>
                                                            ) : pendingCert.church ? (
                                                                <span className="value">{pendingCert.church}</span>
                                                            ) : pendingCert.position ? (
                                                                <span className="value">{pendingCert.position}</span>
                                                            ) : (
                                                                "-"
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* ===== 뒷면: 모바일 신분증 상세 ===== */}
                                            <div className="main-card-face main-card-back"
                                                style={{
                                                    background: "linear-gradient(160deg, rgb(11, 58, 111) 0%, rgb(13, 43, 83) 60%, rgb(8, 34, 63) 100%)",
                                                    color: "#ffffff",
                                                    border: "1px solid rgba(255, 255, 255, 0.7)",
                                                    boxShadow: "0 0px 10px rgba(15, 23, 42, 0.6)",
                                                }}>
                                                <div className="main-back-content"
                                                    style={{
                                                        gap: "0",
                                                        color: "#ffffff"
                                                    }}>
                                                    <h3 className="main-back-title cert"
                                                        style={{
                                                            textAlign: "center",
                                                            background: "transparent",
                                                            color: "#ffffff"
                                                        }}>상세정보</h3>
                                                    <ul className="main-back-list"
                                                        style={{
                                                            padding: "0 18px",
                                                            color: "#ffffff"
                                                        }}>
                                                        <li>
                                                            <span className="k" style={{ color: "#ffffff" }}>닉네임</span>
                                                            <span className="v" style={{ color: "#ffffff" }}>{pendingCert.nickname || "-"}</span>
                                                        </li>
                                                        <li>
                                                            <span className="k" style={{ color: "#ffffff" }}>전화번호</span>
                                                            <span className="v" style={{ color: "#ffffff" }}>
                                                                {(pendingCert.phone || userInfo.phone)
                                                                    ? formatPhoneKOR(pendingCert.phone || userInfo.phone)
                                                                    : "-"}
                                                            </span>
                                                        </li>
                                                        <li>
                                                            <span className="k" style={{ color: "#ffffff" }}>소속</span>
                                                            <span className="v" style={{ color: "#ffffff" }}>{pendingCert.church || "-"}</span>
                                                        </li>
                                                    </ul>

                                                    <div className="main-back-actions">
                                                        {pendingCert.status === "approved" ? (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className="main-back-btn"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleEditClick(pendingCert, 'mobile');
                                                                    }}
                                                                    style={{ background: "#4285f4", color: "#fff" }}
                                                                >
                                                                    정보 수정
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="main-back-btn outline"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDeleteClick(pendingCert, 'mobile');
                                                                    }}
                                                                    style={{ color: "#ff4d4f" }}
                                                                >
                                                                    삭제
                                                                </button>
                                                            </>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </article>
                                </SwiperSlide>
                            )}

                            {/* 다른 자격증 카드들 (맨 뒤로) - visible이 false가 아닌 것만 표시 */}
                            {pendingCerts.filter(cert => cert.visible !== false).map((cert, certIdx) => {
                                const cardIdx = (banners.length || 0) + certIdx;
                                const isFlipped = flippedIdx === cardIdx;
                                return (
                                    <SwiperSlide className="main-slide" key={`pending-cert-${certIdx}`}>
                                        <article
                                            className={`main-card ${cert.status === "pending" ? "main-card-pending" : ""} ${isFlipped ? "is-flipped" : ""}`}
                                            style={{ cursor: cert.status === "pending" ? "default" : "pointer" }}
                                            onClick={() => cert.status === "approved" && toggleFlip(cardIdx)}
                                        >
                                            {/* 관리자 승인 대기 오버레이 (pending일 때만 표시) */}
                                            {cert.status === "pending" && (
                                                <div 
                                                    className="main-card-pending-overlay"
                                                    onClick={() => setApprovalModal({ open: true, cert: cert, type: 'other' })}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <div className="main-card-pending-message">
                                                        관리자 승인 대기중입니다.
                                                    </div>
                                                </div>
                                            )}

                                            {/* 상단 배지 위치 고정 */}
                                            <div className="main-type-badge">
                                                <img src="/img/icon.png" alt="" />
                                                신청자격증
                                            </div>

                                            <div className="main-card-inner">
                                                {/* ===== 앞면 ===== */}
                                                <div
                                                    className="main-card-face main-card-front"
                                                    style={{
                                                        background: GRADE_COLORS[3],
                                                    }}
                                                >
                                                    <div className="main-card-light" />

                                                    {/* 1) 상단 고정(기관/자격명) */}
                                                    <div className="front-top-fixed">
                                                        <h2 className="main-card-title">{cert.issuer || cert.org || "-"}</h2>
                                                        <p className="main-card-greet">{cert.name}</p>
                                                    </div>

                                                    {/* 2) 가운데(세로 가운데 정렬) */}
                                                    <div className="front-center">
                                                        {cert.status === "pending" && (
                                                            <div className="front-row">
                                                                <span className="k">상태</span>
                                                                <span className="v">관리자 승인 대기중</span>
                                                            </div>
                                                        )}
                                                        <div className="front-row">
                                                            <span className="k">교회</span>
                                                            <span className="v">{cert.church || "-"}</span>
                                                        </div>
                                                    </div>

                                                    {/* 3) 하단 CTA */}
                                                    <div className="main-card-footer">
                                                        <button 
                                                            className="main-cta" 
                                                            type="button" 
                                                            style={{ 
                                                                opacity: cert.status === "pending" ? 0.6 : 1, 
                                                                cursor: cert.status === "pending" ? "not-allowed" : "pointer" 
                                                            }}
                                                        >
                                                            상세정보 보기
                                                            <span className="main-cta-arrow" aria-hidden>
                                                                <FontAwesomeIcon icon={faChevronRight} />
                                                            </span>
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* ===== 뒷면 ===== */}
                                                <div className="main-card-face main-card-back">
                                                    <div className="main-back-content">
                                                        <h3 className="main-back-title">상세정보</h3>
                                                        <ul className="main-back-list">
                                                            <li>
                                                                <span className="k">자격번호</span>
                                                                <span className="v">{cert.number || "-"}</span>
                                                            </li>
                                                            <li>
                                                                <span className="k">자격명</span>
                                                                <span className="v">{cert.name}</span>
                                                            </li>
                                                            <li>
                                                                <span className="k">발급기관</span>
                                                                <span className="v">{cert.issuer || cert.org || "-"}</span>
                                                            </li>
                                                            {cert.status === "pending" && (
                                                                <li>
                                                                    <span className="k">상태</span>
                                                                    <span className="v">관리자 승인 대기중</span>
                                                                </li>
                                                            )}
                                                            <li>
                                                                <span className="k">교회</span>
                                                                <span className="v">{cert.church || "-"}</span>
                                                            </li>
                                                        </ul>

                                                        <div className="main-back-actions">
                                                            {cert.status === "approved" ? (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        className="main-back-btn"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleEditClick(cert, 'other');
                                                                        }}
                                                                        style={{ background: "#4285f4", color: "#fff" }}
                                                                    >
                                                                        정보 수정
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="main-back-btn outline"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleDeleteClick(cert, 'other');
                                                                        }}
                                                                        style={{ color: "#ff4d4f" }}
                                                                    >
                                                                        삭제
                                                                    </button>
                                                                </>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </article>
                                    </SwiperSlide>
                                );
                            })}

                            {banners.map((b, i) => {
                                const isFlipped = flippedIdx === i;
                                const isOwned = b.type === "보유자격증";
                                return (
                                    <SwiperSlide className="main-slide" key={i}>
                                        <article
                                            className={`main-card ${isFlipped ? "is-flipped" : ""}`}
                                            onClick={() => toggleFlip(i)}
                                        >
                                            {/* 상단 배지 위치 고정 */}
                                            <div className="main-type-badge">
                                                <img src="/img/icon.png" alt="" />
                                                {b.type}
                                            </div>

                                            <div className="main-card-inner">
                                                {/* ===== 앞면 ===== */}
                                                <div
                                                    className="main-card-face main-card-front"
                                                    style={{
                                                        background:
                                                            GRADE_COLORS[b.grade] ||
                                                            GRADE_COLORS[3],
                                                    }}
                                                >
                                                    <div className="main-card-light" />

                                                    {/* 1) 상단 고정(기관/자격명) */}
                                                    <div className="front-top-fixed">
                                                        <h2 className="main-card-title">{b.org}</h2>
                                                        <p className="main-card-greet">{b.name}</p>
                                                    </div>

                                                    {/* 2) 가운데(세로 가운데 정렬) */}
                                                    <div className="front-center">
                                                        {!isOwned ? (
                                                            <>
                                                                <div className="front-row">
                                                                    <span className="k">상태</span>
                                                                    <span className="v">{b.status}</span>
                                                                </div>
                                                                <div className="front-row">
                                                                    <span className="k">상세정보</span>
                                                                    <span className="v">{b.detail}</span>
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div className="front-row">
                                                                    <span className="k">취득일</span>
                                                                    <span className="v">{b.acquired}</span>
                                                                </div>
                                                                <div className="front-row">
                                                                    <span className="k">갱신기간</span>
                                                                    <span className="v">{b.renew}</span>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>

                                                    {/* 3) 하단 CTA */}
                                                    <div className="main-card-footer">
                                                        {/* {b.cta && b.cta.length > 0 && (
                                                            <button
                                                                className="main-cta"
                                                                type="button"
                                                                onClick={onShareClick}
                                                            >
                                                                {b.cta}
                                                                <span className="main-cta-arrow" aria-hidden>
                                                                    <FontAwesomeIcon icon={faChevronRight} />
                                                                </span>
                                                            </button>
                                                        )} */}
                                                        <button className="main-cta" type="button">
                                                            상세정보 보기
                                                            <span className="main-cta-arrow" aria-hidden>
                                                                <FontAwesomeIcon icon={faChevronRight} />
                                                            </span>
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* ===== 뒷면 ===== */}
                                                <div className="main-card-face main-card-back">
                                                    <div className="main-back-content">
                                                        <h3 className="main-back-title">상세정보</h3>
                                                        <ul className="main-back-list">
                                                            {isOwned ? (
                                                                <>
                                                                    <li>
                                                                        <span className="k">자격번호</span>
                                                                        <span className="v">{b.number}</span>
                                                                    </li>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <li>
                                                                        <span className="k">수험번호</span>
                                                                        <span className="v">{b.number}</span>
                                                                    </li>
                                                                </>
                                                            )}
                                                            <li>
                                                                <span className="k">자격명</span>
                                                                <span className="v">{b.name}</span>
                                                            </li>
                                                            <li>
                                                                <span className="k">발급기관</span>
                                                                <span className="v">{b.org}</span>
                                                            </li>

                                                            {isOwned ? (
                                                                <>
                                                                    <li>
                                                                        <span className="k">취득일</span>
                                                                        <span className="v">{b.acquired}</span>
                                                                    </li>
                                                                    <li>
                                                                        <span className="k">갱신기간</span>
                                                                        <span className="v">{b.renew}</span>
                                                                    </li>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <li>
                                                                        <span className="k">상태</span>
                                                                        <span className="v">{b.status}</span>
                                                                    </li>
                                                                    <li>
                                                                        <span className="k">상세정보</span>
                                                                        <span className="v">{b.detail}</span>
                                                                    </li>
                                                                </>
                                                            )}
                                                        </ul>

                                                        <div className="main-back-actions">
                                                            {!isOwned ? (
                                                                <>
                                                                    <div></div>
                                                                    <button
                                                                        type="button"
                                                                        className="main-back-btn outline"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleFlip(i);
                                                                        }}
                                                                    >
                                                                        닫기
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        className="main-back-btn"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            navigate("/issuance", {
                                                                                state: {
                                                                                    cert: {
                                                                                        number: b.number,
                                                                                        name: b.name,
                                                                                        issuer: b.org,
                                                                                        acquired: b.acquired,
                                                                                        renew: b.renew,
                                                                                        amount: 50000,
                                                                                    },
                                                                                },
                                                                            });
                                                                        }}
                                                                    >
                                                                        자격증 발급신청
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="main-back-btn outline"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleFlip(i);
                                                                        }}
                                                                    >
                                                                        닫기
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </article>
                                    </SwiperSlide>
                                );
                            })}
                        </Swiper>
                    </div>
                </main>

                {shareOpen && (
                    <div
                        className="main-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="share-title"
                        onClick={closeShare}
                    >
                        <div className="main-modal-dialog" onClick={(e) => e.stopPropagation()}>
                            <div className="main-modal-body">
                                <h3 id="share-title" className="main-modal-title">공유되었습니다.</h3>
                                <p className="main-modal-desc">성공적으로 공유 처리되었습니다.</p>
                            </div>
                            <div className="main-modal-actions">
                                <button className="main-modal-btn" onClick={closeShare}>확인</button>
                            </div>
                        </div>
                    </div>
                )}

            {/* ===== 승인/거절 모달 ===== */}
            {approvalModal.open && (
                <div 
                    className="main-modal" 
                    role="dialog" 
                    aria-modal="true" 
                    onClick={() => setApprovalModal({ open: false, cert: null, type: null })}
                >
                    <div className="main-modal-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="main-modal-body">
                            <h3 className="main-modal-title">자격증 승인</h3>
                            <p className="main-modal-desc">
                                {approvalModal.cert?.name} 자격증을 승인하시겠습니까?
                            </p>
                        </div>
                        <div className="main-modal-actions" style={{ display: "flex", gap: "10px" }}>
                            <button 
                                className="main-modal-btn" 
                                onClick={handleReject}
                                style={{ 
                                    flex: 1, 
                                    background: "#ff4d4f", 
                                    color: "#fff" 
                                }}
                            >
                                거절
                            </button>
                            <button 
                                className="main-modal-btn" 
                                onClick={handleApprove}
                                style={{ flex: 1, background: "#4285f4", color: "#fff" }}
                            >
                                승인
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== 수정 모달 ===== */}
            {editModal.open && editModal.draft && (
                <div 
                    className="issuance-modal-overlay" 
                    role="dialog" 
                    aria-modal="true" 
                    onClick={() => setEditModal({ open: false, cert: null, type: null, draft: null })}
                >
                    <div className="issuance-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "600px", maxHeight: "600px", width: "90%" }}>
                        <div className="issuance-modal-bar">
                            <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "900" }}>자격증 정보 수정</h3>
                            <button 
                                className="issuance-close"
                                onClick={() => setEditModal({ open: false, cert: null, type: null, draft: null })}
                            >
                                ✕
                            </button>
                        </div>
                        <div className="issuance-modal-body" style={{ padding: "20px", overflowY: "auto", maxHeight: "calc(600px - 60px)" }}>
                            {/* 자격증 정보(읽기전용) */}
                            <div className="issuance-card">
                                <div className="issuance-row">
                                    <label>자격증</label>
                                    <div className="issuance-value">{editModal.draft.name || "-"}</div>
                                </div>
                                <div className="issuance-row">
                                    <label>번호</label>
                                    <div className="issuance-value">{editModal.draft.number || "-"}</div>
                                </div>
                                <div className="issuance-row" style={{ alignItems: "flex-start" }}>
                                    <label>발급기관</label>
                                    <div
                                        className="issuance-value"
                                        style={{ fontSize: "14px", whiteSpace: "pre-line" }}
                                    >
                                        {editModal.draft.issuer || editModal.draft.org || "-"}
                                    </div>
                                </div>
                            </div>

                            {/* 상세정보 */}
                            <div className="issuance-section" style={{ marginTop: "14px" }}>
                                {editModal.type === 'mobile' ? (
                                    <>
                                        <div className="issuance-row">
                                            <label htmlFor="edit-position">직책</label>
                                            <input
                                                id="edit-position"
                                                className="issuance-input"
                                                type="text"
                                                value={editModal.draft.position || ""}
                                                onChange={(e) => setEditModal(prev => ({ ...prev, draft: { ...prev.draft, position: e.target.value } }))}
                                                placeholder="직책을 입력하세요"
                                            />
                                        </div>
                                        <div className="issuance-row">
                                            <label htmlFor="edit-yearAssociation">연회</label>
                                            <input
                                                id="edit-yearAssociation"
                                                className="issuance-input"
                                                type="text"
                                                value={editModal.draft.yearAssociation || ""}
                                                onChange={(e) => setEditModal(prev => ({ ...prev, draft: { ...prev.draft, yearAssociation: e.target.value } }))}
                                                placeholder="연회를 입력하세요"
                                            />
                                        </div>
                                        <div className="issuance-row">
                                            <label htmlFor="edit-localAssociation">지방회</label>
                                            <input
                                                id="edit-localAssociation"
                                                className="issuance-input"
                                                type="text"
                                                value={editModal.draft.localAssociation || ""}
                                                onChange={(e) => setEditModal(prev => ({ ...prev, draft: { ...prev.draft, localAssociation: e.target.value } }))}
                                                placeholder="지방회를 입력하세요"
                                            />
                                        </div>
                                    </>
                                ) : null}
                                <div className="issuance-row">
                                    <label htmlFor="edit-church">교회</label>
                                    <input
                                        id="edit-church"
                                        className="issuance-input"
                                        type="text"
                                        value={editModal.draft.church || ""}
                                        onChange={(e) => setEditModal(prev => ({ ...prev, draft: { ...prev.draft, church: e.target.value } }))}
                                        placeholder="교회를 입력하세요"
                                    />
                                </div>
                            </div>

                            {/* 이미지 업로드 (모바일 신분증만) */}
                            {editModal.type === 'mobile' && (
                                <div className="issuance-section" style={{ marginTop: "14px" }}>
                                    <div className="issuance-image-upload">
                                        <label htmlFor="edit-image" className="issuance-image-label">이미지</label>
                                        <div className="issuance-image-container">
                                            {editModal.draft.image ? (
                                                <div className="issuance-image-preview-wrapper">
                                                    <img 
                                                        src={editModal.draft.image} 
                                                        alt="미리보기" 
                                                        className="issuance-image-preview"
                                                    />
                                                    <button
                                                        type="button"
                                                        className="issuance-image-remove"
                                                        onClick={handleEditImageRemove}
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="issuance-image-upload-area">
                                                    <input
                                                        ref={editImageInputRef}
                                                        id="edit-image"
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleEditImageChange}
                                                        style={{ display: "none" }}
                                                    />
                                                    <label
                                                        htmlFor="edit-image"
                                                        className="issuance-image-upload-button"
                                                    >
                                                        <span>+</span>
                                                        <span>이미지 선택</span>
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div style={{ padding: "16px 20px", borderTop: "1px solid #eee", display: "flex", gap: "10px" }}>
                            <button 
                                className="issuance-btn" 
                                onClick={() => setEditModal({ open: false, cert: null, type: null, draft: null })}
                                style={{ flex: 1, background: "#999", color: "#fff" }}
                            >
                                취소
                            </button>
                            <button 
                                className="issuance-btn" 
                                onClick={handleEditSave}
                                style={{ flex: 1 }}
                            >
                                저장
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== 삭제 확인 모달 ===== */}
            {deleteModal.open && (
                <div 
                    className="main-modal" 
                    role="dialog" 
                    aria-modal="true" 
                    onClick={() => setDeleteModal({ open: false, cert: null, type: null })}
                >
                    <div className="main-modal-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="main-modal-body">
                            <h3 className="main-modal-title">자격증 삭제</h3>
                            <p className="main-modal-desc">
                                {deleteModal.cert?.name} 자격증을 삭제하시겠습니까?
                            </p>
                            <p className="main-modal-desc" style={{ marginTop: "8px", fontSize: "14px", color: "#999" }}>
                                삭제된 자격증은 복구할 수 없습니다.
                            </p>
                        </div>
                        <div className="main-modal-actions" style={{ display: "flex", gap: "10px" }}>
                            <button 
                                className="main-modal-btn" 
                                onClick={() => setDeleteModal({ open: false, cert: null, type: null })}
                                style={{ flex: 1, background: "#999", color: "#fff" }}
                            >
                                취소
                            </button>
                            <button 
                                className="main-modal-btn" 
                                onClick={handleDeleteConfirm}
                                style={{ flex: 1, background: "#ff4d4f", color: "#fff" }}
                            >
                                삭제
                            </button>
                        </div>
                    </div>
                </div>
            )}

                <div style={{ height: 100 }} />
            </div>
        </div>
    );
}
